<template>
  <div class="staff-page">
    <header class="banner">
      <p class="banner-name">三高体检·内勤登录</p>
      <p class="banner-id">{{ taikangMode ? '使用手机号和机构编码登录指定机构' : '仅限机构内勤人员使用' }}</p>
    </header>

    <main class="form-card">
      <van-field
        v-model="loginForm.userPhone"
        type="tel"
        label="手机号"
        placeholder="请输入手机号"
        maxlength="11"
        clearable
      />
      <van-field
        v-if="taikangMode"
        v-model.trim="loginForm.orgId"
        label="机构编码"
        placeholder="请输入机构ID（orgId）"
        inputmode="numeric"
        maxlength="19"
        clearable
        @keyup.enter="handleLogin"
      />
      <van-field
        v-else
        v-model="loginForm.securityCode"
        label="验证码"
        placeholder="请输入验证码"
        maxlength="6"
        clearable
      >
        <template #button>
          <button
            type="button"
            class="code-button"
            :disabled="!canGetCode"
            @click="handleGetCode"
          >
            {{ codeButtonText }}
          </button>
        </template>
      </van-field>

      <button
        type="button"
        class="submit-button"
        :disabled="!canLogin || submitting"
        @click="handleLogin"
      >
        {{ submitting ? "登录中..." : "登录" }}
      </button>
    </main>

    <van-overlay :show="submitting" z-index="2000">
      <van-loading color="#fff" size="36" vertical>处理中...</van-loading>
    </van-overlay>
    <org-picker v-if="!taikangMode && pendingLogin" :organizations="pendingLogin.organizations" :value="selectedOrgId" :busy="switchingOrg" title="选择登录机构" cancelable @select="selectedOrgId = $event" @confirm="confirmOrg" @cancel="pendingLogin = null" />
  </div>
</template>

<script>
import { Toast } from "vant";
import { sendSanGaoStaffCode, loginSanGaoStaff, switchSanGaoStaffOrg } from "@/api/sanGaoStaff";
import OrgPicker from './OrgPicker';
import { STAFF_TOKEN_KEY, STAFF_INFO_KEY, saveStaffSession } from './staff-session';
import { isTaikangStaffMode } from '@/utils/sanGaoStaffMode';


const PHONE_REGEX = /^1[3-9]\d{9}$/;
const CODE_REGEX = /^\d{6}$/;

export default {
  name: "SanGaoStaffLogin",
  components: { OrgPicker },
  data() {
    return {
      loginForm: {
        userPhone: "",
        securityCode: "",
        orgId: typeof this.$route.query.orgId === 'string' ? this.$route.query.orgId : '',
      },
      countdown: 0,
      submitting: false,
      pendingLogin: null,
      selectedOrgId: null,
      switchingOrg: false,
    };
  },
  computed: {
    taikangMode() { return isTaikangStaffMode(this.$route.query); },
    validOrgCode() { const code = this.loginForm.orgId.trim(); return /^\d+$/.test(code) && Number.isSafeInteger(Number(code)) && Number(code) > 0; },
    canGetCode() {
      return !this.taikangMode && PHONE_REGEX.test(this.loginForm.userPhone) && this.countdown === 0;
    },
    codeButtonText() {
      return this.countdown > 0 ? `${this.countdown}s 后重发` : "获取验证码";
    },
    canLogin() {
      return (
        PHONE_REGEX.test(this.loginForm.userPhone) &&
        (this.taikangMode ? this.validOrgCode : CODE_REGEX.test(this.loginForm.securityCode))
      );
    },
  },
  watch: { taikangMode() { this.pendingLogin = null; this.selectedOrgId = null; this.loginForm.securityCode = ''; } },
  methods: {
    startCountdown() {
      this.countdown = 60;
      const timer = setInterval(() => {
        this.countdown -= 1;
        if (this.countdown <= 0) {
          clearInterval(timer);
        }
      }, 1000);
    },
    async handleGetCode() {
      if (!this.canGetCode) return;
      const res = await sendSanGaoStaffCode({ userPhone: this.loginForm.userPhone });
      if (res && res.code === 200) {
        Toast("验证码已发送");
        this.startCountdown();
      } else {
        Toast((res && res.msg) || "发送失败，请重试");
      }
    },
    async handleLogin() {
      if (!this.canLogin || this.submitting) return;
      const taikangMode = this.taikangMode;
      const orgId = Number(this.loginForm.orgId);
      this.submitting = true;
      try {
        const res = await loginSanGaoStaff(taikangMode ? {
          userPhone: this.loginForm.userPhone,
          orgId,
          taikangMode: true,
        } : {
          userPhone: this.loginForm.userPhone,
          securityCode: this.loginForm.securityCode,
        });
        if (this._isDestroyed || taikangMode !== this.taikangMode) return;
        if (res && res.code === 200 && res.data) {
          if (taikangMode && (res.data.taikangMode !== true || Number(res.data.orgId) !== orgId)) {
            Toast('机构登录信息不匹配，请重试或联系管理员');
            return;
          }
          localStorage.removeItem(STAFF_TOKEN_KEY);
          localStorage.removeItem(STAFF_INFO_KEY);
          if (!taikangMode && (res.data.organizations || []).length > 1) {
            this.pendingLogin = res.data;
            this.selectedOrgId = null;
          } else {
            saveStaffSession(res.data);
            Toast("登录成功");
            this.$router.replace({ path: '/sanGao/staff/index', query: taikangMode ? { taikang: '1' } : this.$route.query });
          }
        } else {
          Toast((res && res.msg) || "登录失败");
        }
      } catch (e) {
        Toast("登录失败，请重试");
      } finally {
        this.submitting = false;
      }
    },
    async confirmOrg() {
      if (this.taikangMode || !this.pendingLogin || !this.selectedOrgId || this.switchingOrg) return;
      const pendingLogin = this.pendingLogin;
      this.switchingOrg = true;
      try {
        const res = await switchSanGaoStaffOrg(this.selectedOrgId, pendingLogin.token);
        if (this._isDestroyed || this.taikangMode || this.pendingLogin !== pendingLogin) return;
        if (!res || res.code !== 200 || !res.data) throw new Error((res && res.msg) || '切换机构失败');
        saveStaffSession(res.data);
        this.pendingLogin = null;
        Toast('登录成功');
        this.$router.replace('/sanGao/staff/index');
      } catch (e) {
        Toast(e.message || '切换机构失败，请重试');
      } finally {
        this.switchingOrg = false;
      }
    },
  },
};
</script>

<style lang="less" scoped>
* {
  box-sizing: border-box;
}

button {
  font: inherit;
}

.staff-page {
  position: relative;
  min-height: 100vh;
  padding: 24px 16px 32px;
  background: linear-gradient(180deg, #0874ca 0%, #4ba8e0 28%, #dff6ff 60%, #dff6ff 100%);
  color: #163b5c;
}

.banner {
  margin: 0 0 18px;
  padding: 18px 18px 16px;
  border: 1px solid rgba(255, 255, 255, 0.65);
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.96);
  box-shadow: 0 6px 18px rgba(12, 91, 147, 0.12);
  text-align: center;
}

.banner-name {
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  line-height: 1.4;
  color: #163b5c;
}

.banner-id {
  margin: 6px 0 0;
  font-size: 12px;
  color: #6f8aa1;
}

.form-card {
  padding: 20px 14px 18px;
  border-radius: 16px;
  background: #fff;
  box-shadow: 0 8px 24px rgba(12, 91, 147, 0.1);
}

/deep/ .van-cell {
  padding: 12px 6px;
  border-bottom: 1px solid #eef4f9;
}

.code-button {
  padding: 6px 12px;
  border: 0;
  border-radius: 18px;
  font-size: 13px;
  color: #168de0;
  background: rgba(22, 141, 224, 0.1);
  cursor: pointer;
}

.code-button:disabled {
  color: #b8c6d2;
  background: #f1f5f8;
  cursor: not-allowed;
}

.submit-button {
  display: block;
  width: 100%;
  height: 46px;
  margin-top: 22px;
  border: 0;
  border-radius: 23px;
  font-size: 15px;
  font-weight: 600;
  color: #fff;
  background: linear-gradient(100deg, #19a2e5, #146bd2);
  box-shadow: 0 6px 15px rgba(17, 112, 199, 0.22);
  cursor: pointer;
}

.submit-button:disabled {
  color: rgba(255, 255, 255, 0.85);
  background: #b4cad9;
  box-shadow: none;
  cursor: not-allowed;
}

/deep/ .van-loading {
  color: #fff;
}
</style>
